import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isPreviewMode } from "@/lib/previewMode";

type OrgRole = "institution_admin" | "teacher" | "parent" | "student";

/**
 * Gates a route to users who hold at least one active membership of the
 * given role in any organization, OR who are platform admins.
 * Used to power /institution, /teacher, and /parent surfaces.
 */
export function RoleRoute({
  role,
  children,
}: {
  role: OrgRole;
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAllowed(null);
      return;
    }
    (async () => {
      // Platform admin bypass
      const { data: adminRow } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (adminRow) {
        if (!cancelled) setAllowed(true);
        return;
      }
      const { data } = await supabase
        .from("organization_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("member_role", role)
        .eq("status", "active")
        .limit(1);
      if (!cancelled) setAllowed((data?.length ?? 0) > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, role]);

  if (loading || (user && allowed === null)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export const InstitutionRoute = ({ children }: { children: React.ReactNode }) => (
  <RoleRoute role="institution_admin">{children}</RoleRoute>
);
export const TeacherRoute = ({ children }: { children: React.ReactNode }) => (
  <RoleRoute role="teacher">{children}</RoleRoute>
);
export const ParentRoute = ({ children }: { children: React.ReactNode }) => (
  <RoleRoute role="parent">{children}</RoleRoute>
);

/**
 * Gates the live /schools surfaces to anyone holding ANY active organization
 * membership (institution_admin, teacher, parent or student) — platform admins
 * pass through via the admin bypass inside RoleRoute.
 */
export function OrgMemberRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  // Dev-only door for screenshotting the /schools suite without a membership.
  // Inert in production builds — see src/lib/previewMode.ts.
  const preview = isPreviewMode();

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAllowed(null);
      return;
    }
    (async () => {
      const { data: adminRow } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (adminRow) {
        if (!cancelled) setAllowed(true);
        return;
      }
      const { data } = await supabase
        .from("organization_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1);
      if (!cancelled) setAllowed((data?.length ?? 0) > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (preview) return <>{children}</>;
  if (loading || (user && allowed === null)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
